import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as types from '../../../constants/actionTypes';
import { actions as filesActions } from '../../../actions/filesActions';
import PropTypes from 'prop-types';
import DrawerV6 from '../../Common/Drawer/DrawerV6/DrawerV6';
import { MOBILE_WINDOW_BREAKPOINT, FILE_STATUSES, MAX_FILE_SIZE, DATETIME_FORMAT } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Icon from '../../Common/Icon';
import InputText from '../../Common/InputText';
import TextArea from '../../Common/TextArea';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Toggle from '../../Common/Toggle/Toggle';
import Modal from '../../Common/Modal/Modal';
import DropZone from 'react-dropzone';
import Moment from 'moment';
import './LocationFileManagerDrawer.scss';

export class LocationFileManagerDrawer extends Component {
	constructor(props, context) {
		super(props, context);

		const fileStatuses = [
			{ name: 'Pending', id: FILE_STATUSES.Pending.toString() },
			{ name: 'Approved', id: FILE_STATUSES.Approved.toString() },
			{ name: 'Denied', id: FILE_STATUSES.Denied.toString() },
		];

		this.state = {
			showConfirmDeleteModal: false,
			isFormValid: true,
			isLoading: false,
			files: [],
			departments: [],
			projects: [],
			tasks: [],
			fileStatuses: fileStatuses,
			userWantsToOverwriteFile: false,
			droppedFile: null,
			isDropZoneActive: false,
			editNames: false,
			editMode: false,
			fileNames: [],

			// update all files with these before saving
			description: '',
			departmentId: '',
			projectId: '',
			locationTaskId: '',
			fileStatusId: '',
			isActive: true,
			isProjectWidgetActive: false,
		};

		this.dropzoneRef = React.createRef();
	}

	isMobile = () => {
		return this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT;
	};

	componentDidMount = async () => {
		await Promise.all([this.loadFilters(), this.loadFile()]);
	};

	loadFile = async () => {
		try {
			if (this.props.fileId && this.props.fileId !== '0') {
				const response = await this.props.filesActions.loadFile(this.props.fileId);
				if (response.type === types.LOAD_FILE_SUCCESS) {
					let file = response.payload.customerFile;
					let deptId = '';
					if (file.departmentId) {
						deptId = file.departmentId.toString();
					}

					let projId = '';
					if (file.projectId) {
						projId = file.projectId.toString();
					}

					let taskId = '';
					if (file.locationTaskId) {
						taskId = file.locationTaskId.toString();
					}

					await this.loadTaskFilters(deptId, projId);

					let statusId = file.fileStatusId.toString();

					this.setState({
						description: file.description,
						departmentId: deptId,
						projectId: projId,
						locationTaskId: taskId,
						fileStatusId: statusId,
						approverName: file.approverName,
						isActive: file.isActive,
						files: [
							{
								id: file.id,
								name: file.name,
								fileStatusId: file.fileStatusId,
								storageName: file.storageName,
							},
						],
						isLoading: false,
						editMode: true,
					});
				} else if (response.type === types.LOAD_FILE_FAILURE) {
					Notification('error', 'Error', response.payload.message);
					this.setState({ isLoading: false });
				}
			} else if (this.props.droppedFile) {
				// If a file was dropped to open this drawer, then automatically
				// set that file as the file to save
				this.setState({
					file: {
						...this.state.file,
						name: this.props.droppedFile.name,
						file: this.props.droppedFile,
					},
				});
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	loadFilters = async () => {
		try {
			const response = await this.props.filesActions.loadLocationFileFilters(this.props.locationKey);
			if (response.type === types.LOAD_LOCATION_FILE_FILTERS_SUCCESS) {
				this.setState({
					departments: response.payload.departments,
					projects: response.payload.projects,
					isProjectWidgetActive: response.payload.isProjectWidgetActive,
				});
			} else if (response.type === types.LOAD_LOCATION_FILE_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	loadTaskFilters = async (deptId, projId) => {
		if (!deptId) {
			return;
		}
		try {
			const response = await this.props.filesActions.loadLocationFileTaskFilters(deptId, projId, this.props.locationKey);
			if (response.type === types.LOAD_LOCATION_FILE_TASK_FILTERS_SUCCESS) {
				this.setState({
					tasks: response.payload.tasks,
				});
			} else if (response.type === types.LOAD_LOCATION_FILE_TASK_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	validateForm = () => {
		const { departmentId, projectId, files, isProjectWidgetActive } = this.state;

		const isNewFile = this.props.fileId && this.props.fileId !== '0' ? false : true;
		const isMissingFile = !files || files.length === 0;
		const isFileValid = this.isMobile() || (!this.isMobile() && isNewFile && !isMissingFile) || (!this.isMobile() && !isNewFile);

		if (!isFileValid) {
			Notification('error', '', 'File is required');
			return false;
		}

		if (files.length > 25) {
			Notification('error', '', 'Only 25 files are allowed at a time.');
			return false;
		}

		// find the total size of all files
		const arSizes = files.map((file) => file.size);
		const reducer = (accumulator, currentValue) => accumulator + currentValue;
		const totalSize = arSizes.reduce(reducer);

		if (totalSize > MAX_FILE_SIZE) {
			Notification('error', '', 'Files cannot exceed 100MB in size. You total size is: ' + totalSize);
			return false;
		}

		// make sure all files have names
		let hasBlankName = false;
		files.forEach((file) => {
			if (!file.name || file.name.length === 0) {
				hasBlankName = true;
			}
		});

		if (hasBlankName) {
			return false;
		}

		// Make project required if isProjectWidgetActive, else, mark the task id as required
		const isProjectRequired = isProjectWidgetActive ? projectId && projectId.trim().length > 0 : true;

		const isFormValid =
			departmentId &&
			departmentId.trim().length > 0 &&
			isProjectRequired &&
			// File is required on a new upload, but you can update a file
			// entry without changing the file.  File is NOT required if isMobile,
			// because we do not allow uploads via mobile.
			isFileValid;
		return isFormValid;
	};

	onSave = async () => {
		if (!this.validateForm()) {
			this.setState({ isFormValid: false });
			return;
		}

		const commonAttributes = {
			description: this.state.description,
			departmentId: this.state.departmentId,
			projectId: this.state.projectId,
			locationTaskId: this.state.locationTaskId,
			fileStatusId: this.state.fileStatusId,
			isActive: this.state.isActive,
			isVisibleInRepository: false,
			locationKey: this.props.locationKey,
		};

		try {
			this.setState({ isLoading: true });

			let response;
			if (this.state.editMode) {
				const file = this.state.files[0];
				const updatedFile = {
					...file,
					...commonAttributes,
				};
				response = await this.props.filesActions.saveFile(updatedFile, false);
			} else {
				response = await this.props.filesActions.saveMultipleNewFiles(this.state.files, commonAttributes);
			}

			if (response.type === types.CREATE_MULTIPLE_FILES_SUCCESS || response.type === types.UPDATE_FILE_SUCCESS) {
				Notification('success', 'Success', 'File saved successfully');
				this.props.onComplete();
			} else if (response.type === types.CREATE_MULTIPLE_FILES_FAILURE || response.type === types.UPDATE_FILE_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isLoading: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	onDeleteFile = async () => {
		try {
			this.setState({ isLoading: true, showConfirmDeleteModal: false });
			const request = { id: this.state.files[0].id };
			const response = await this.props.filesActions.deleteFile(request);
			if (response.type === types.DELETE_FILE_SUCCESS) {
				Notification('success', 'Success', 'File deleted successfully');
				this.props.onComplete();
			} else if (response.type === types.DELETE_FILE_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isLoading: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	onShowConfirmDeleteFileModal = () => {
		this.setState({ showConfirmDeleteModal: true });
	};

	onCancelDeleteFile = () => {
		this.setState({ showConfirmDeleteModal: false });
	};

	onFormChange = (name, value) => {
		this.setState({ [name]: value });
	};

	onTextChange = (e) => {
		this.setState({ [e.target.name]: e.target.value });
	};

	onToggleChange = (name, value) => {
		this.onFormChange(name, value);
	};

	onDepartmentChange = (value) => {
		this.setState({ departmentId: value.toString() }, () => {
			// load tasks for the selected department
			this.loadTaskFilters(value, this.state.projectId);
		});
	};

	onProjectChange = (value) => {
		this.setState({ projectId: value.toString() }, () => {
			// load tasks for the selected project
			this.loadTaskFilters(this.state.departmentId, value);
		});
	};

	onMultiSelectChange = (value, name) => {
		this.setState({ [name]: value.toString() });
	};

	downloadFile = async () => {
		try {
			this.setState({ isLoading: true });
			await this.props.filesActions.download(this.state.files[0].storageName);
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isLoading: false });
		}
	};

	onOverwriteClick = () => {
		this.setState({ userWantsToOverwriteFile: true });
	};

	uploadFile = (e) => {
		e.preventDefault();
		this.dropzoneRef.current.open();
	};

	onDrop = (droppedFiles) => {
		if (droppedFiles && droppedFiles.length > 0) {
			let newFiles = this.state.files;

			droppedFiles.forEach((file) => {
				newFiles.push({
					id: this.state.editMode ? this.state.files[0].id : 0,
					name: this.state.editMode ? this.state.files[0].name : file.name.substr(0, file.name.lastIndexOf('.')),
					storageName: this.state.editMode ? this.state.files[0].storageName : '',
					file: file,
				});
			});

			this.setState({ files: newFiles, isDropZoneActive: false });
		}
	};

	onDropRejected = () => {
		Notification('error', 'Error', 'Invalid file type(s)');
	};

	onDragEnter = () => {
		this.setState({ isDropZoneActive: true });
	};

	onDragLeave = () => {
		this.setState({ isDropZoneActive: false });
	};

	editNames = () => {
		this.setState({ editNames: true });
	};

	updateFileName = (e, index) => {
		const newFiles = [...this.state.files];

		newFiles[index].name = e.target.value;
		this.setState({ files: newFiles });
	};

	removeByName = (index) => {
		const newFiles = [...this.state.files];

		newFiles.splice(index, 1);
		this.setState({ files: newFiles });
	};

	render() {
		const className = this.state.isFormValid ? 'subscription-file-upload-form' : 'subscription-file-upload-form was-validated';
		const isExistingFile = this.state.editMode;
		const buttonText = isExistingFile ? 'Update File' : 'Save File';

		const showAnyFileUploadOptions = !this.isMobile();
		const showDroppedFileIndicator = showAnyFileUploadOptions && this.state.droppedFile;
		const showOverwriteButton = showAnyFileUploadOptions && isExistingFile && !this.state.userWantsToOverwriteFile;

		const showFileInput = showAnyFileUploadOptions && ((isExistingFile && this.state.userWantsToOverwriteFile) || !isExistingFile);

		return (
			<DrawerV6 isWide={false} isMobile={this.isMobile()} title='Location File' onSaveClick={this.onSave} saveText={buttonText} onCancelClick={this.props.onCancel}>
				<LoadingIndicator isLoading={this.state.isLoading}>
					<form className={className} noValidate>
						<div className='row'>
							<div className='col'>
								{showFileInput && (
									<DropZone
										ref={this.dropzoneRef}
										accept={{
											'image/jpeg': [],
											'image/png': [],
											'image/gif': [],
											'application/pdf': [],
											'application/vnd.ms-excel': [],
											'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
											'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
											'text/plain': [],
											'text/csv': [],
											'application/zip': [],
											'application/vnd.rar': [],
										}}
										multiple={!this.state.editMode}
										onDropAccepted={this.onDrop}
										onDropRejected={this.onDropRejected}
										onDragEnter={this.onDragEnter}
										onDragLeave={this.onDragLeave}>
										{({ getRootProps, getInputProps, isDragActive }) => (
											<div
												{...getRootProps({
													className: `dropzone ${isDragActive ? 'active' : ''}`,
												})}>
												<input {...getInputProps()} />
												{isDragActive && (
													<div className='dropzone-overlay'>
														<div className='overlay-content'>
															<Icon name='cloud-upload-alt' />
															<p>Drop your file(s) here...</p>
														</div>
													</div>
												)}
												<div className='box text-center'>
													<Icon name='cloud-upload-alt' />
													<p>
														<strong>Choose a file(s)</strong> or drag them here
													</p>
													{this.state.files?.length > 0 ? (
														<p>{this.state.files.length} file(s) selected</p>
													) : (
														<p>No files selected</p>
													)}
												</div>
											</div>
										)}
									</DropZone>
								)}

								{this.state.files && this.state.files.length > 1 && (
									<ul className='list-group'>
										{this.state.files.map((file, index) => {
											return (
												<li className='list-group-item' key={index}>
													<div className='file-name-text'>
														<InputText
															name={'File ' + (index + 1)}
															label={'File ' + (index + 1) + ' Name'}
															type='text'
															value={file.name}
															onChange={(text) => this.updateFileName(text, index)}
															isRequired={true}
															maxLength={100}
														/>
													</div>
													<div className='file-name-icon' onClick={() => this.removeByName(index)}>
														<Icon name='trash-alt' />
													</div>
												</li>
											);
										})}
									</ul>
								)}

								{this.state.files && this.state.files.length === 1 && (
									<InputText
										name='name'
										label='Name'
										type='text'
										value={this.state.files[0].name}
										onChange={(text) => this.updateFileName(text, 0)}
										isRequired={true}
										maxLength={100}
									/>
								)}

								<TextArea
									rows={2}
									name='description'
									label='Description'
									placeholder='Description'
									value={this.state.description}
									onChange={this.onTextChange}
									maxLength={500}
								/>

								<MultiSelectBox
									name='departmentId'
									label='Department'
									idField='id'
									valueField='name'
									options={this.state.departments}
									defaultValue='Select a Department'
									selectedValues={this.state.departmentId.toString()}
									onChange={this.onDepartmentChange}
									isRequired={true}
									errorMessage='Department is required'
									isMulti={false}
								/>

								{this.state.isProjectWidgetActive && (
									<MultiSelectBox
										name='projectId'
										label='Project'
										idField='id'
										valueField='name'
										options={this.state.projects}
										defaultValue='Select a Project'
										selectedValues={this.state.projectId.toString()}
										onChange={this.onProjectChange}
										isRequired={true}
										errorMessage='Project is required'
										isMulti={false}
									/>
								)}

								<MultiSelectBox
									name='locationTaskId'
									label='Task'
									idField='id'
									valueField='name'
									options={this.state.tasks}
									defaultValue='Select a Task'
									selectedValues={this.state.locationTaskId.toString()}
									onChange={this.onMultiSelectChange}
									isRequired={false}
									errorMessage='Task is required'
									isMulti={false}
									isDisabled={
										this.state.departmentId && (this.state.isProjectWidgetActive ? this.state.projectId : true) ? false : true
									}
								/>

								{this.state.editMode && (
									<MultiSelectBox
										name='fileStatusId'
										label='Status'
										idField='id'
										valueField='name'
										options={this.state.fileStatuses}
										selectedValues={this.state.fileStatusId.toString()}
										onChange={this.onMultiSelectChange}
										isRequired={true}
										errorMessage='Status is required'
										isMulti={false}
									/>
								)}
								{this.state.editMode &&
									this.state.files[0].approverName &&
									this.state.files[0].fileStatusId === FILE_STATUSES.Approved.toString() && (
										<div className='approver'>
											Approved by {this.state.files[0].approverName} on{' '}
											{Moment(this.state.files[0].fileStatusModifiedDate).format(DATETIME_FORMAT)}
										</div>
									)}
								{this.state.editMode &&
									this.state.files[0].approverName &&
									this.state.files[0].fileStatusId === FILE_STATUSES.Denied.toString() && (
										<div className='approver'>
											Denied by {this.state.files[0].approverName} on{' '}
											{Moment(this.state.files[0].fileStatusModifiedDate).format(DATETIME_FORMAT)}
										</div>
									)}

								{this.state.editMode && (
									<Toggle name='isActive' text='Active' checked={this.state.isActive} onChange={this.onToggleChange} />
								)}

								{showDroppedFileIndicator && <div className='dropped-file'>File Name: {this.state.droppedFile.name}</div>}

								{showOverwriteButton && (
									<button className='overwrite-file-button' onClick={this.onOverwriteClick}>
										Overwrite Existing File
									</button>
								)}

								{this.state.editMode && (
									<span className='download-link' onClick={this.downloadFile}>
										<Icon name='download' />
										Download
									</span>
								)}

								{this.state.editMode && (
									<div>
										<br />
										<button type='button' onClick={this.onShowConfirmDeleteFileModal} className='btn btn-danger'>
											<Icon name='trash-alt' />
										</button>
									</div>
								)}
								{this.state.showConfirmDeleteModal && (
									<Modal
										id='confirmDelete'
										title='Confirm Deletion'
										body={<span>Are you sure you want to delete this file?</span>}
										primaryButtonText='Delete'
										secondaryButtonText='Cancel'
										onPrimaryClick={this.onDeleteFile}
										onSecondaryClick={this.onCancelDeleteFile}
										onCloseClick={this.onCancelDeleteFile}
									/>
								)}
							</div>
						</div>
					</form>
				</LoadingIndicator>
			</DrawerV6>
		);
	}
}

LocationFileManagerDrawer.propTypes = {
	filesActions: PropTypes.object.isRequired,
	onComplete: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	locationKey: PropTypes.string.isRequired,
	windowWidth: PropTypes.number,
	fileId: PropTypes.string,
	droppedFile: PropTypes.object,
};

function mapDispatchToProps(dispatch) {
	return {
		filesActions: bindActionCreators(filesActions, dispatch),
	};
}

function mapStateToProps(state) {
	return {
		windowWidth: state.window.width,
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationFileManagerDrawer);
